import React, { useEffect, useState, useMemo } from "react";
import { useLocation, Link } from "react-router-dom";
import {
  Trash2,
  ShieldAlert,
  ShieldCheck,
  Layers,
  ArrowUpRight,
  X,
  CheckCircle2,
} from "lucide-react";
import VNGStep from "../components/VNGStep";
import ReadinessGauge from "../components/ReadinessGauge";
import CircleGlow from "../components/CircleGlow";
import { vngSteps } from "../data/vngSteps";
import { leeuwardenSystems } from "../data/leeuwardenSystems";
import {
  loadAssessments,
  deleteAssessment,
  RISK_LABEL,
} from "../lib/assessmentStore";

/**
 * Platform — THE COCKPIT (admin / assessment view).
 *
 *   1. Header with eyebrow + title.
 *   2. Readiness gauges per VNG step (Leeuwarden snapshot).
 *   3. Beheerde systemen — every assessment in scope, with delete actions for
 *      user-added systems (Leeuwarden seeds are kept as the case-study baseline).
 *   4. VNG-lifecycle timeline (8 steps).
 */

// Journey to Compliance — the gauges visualise growth, not decay. The
// gemeente Leeuwarden starts at a low score during initiation and is
// brought to full audit-proof readiness by the time the system reaches
// the evaluation phase, under continuous guidance from RiskaiΛ.
const READINESS = {
  initiatie: 15,
  "data-analyse": 28,
  ontwerp: 42,
  ontwikkeling: 55,
  validatie: 68,
  implementatie: 82,
  monitoring: 94,
  evaluatie: 100,
};

const RISK_TONE = {
  high: "#F08080",
  medium: "#F4C430",
  low: "#9FE2BF",
};

const Platform = () => {
  const { hash } = useLocation();
  const [userSystems, setUserSystems] = useState([]);
  const [confirmTarget, setConfirmTarget] = useState(null); // {id, name} | null
  const [toast, setToast] = useState(null); // {label, tone} | null

  useEffect(() => {
    setUserSystems(loadAssessments());
  }, []);

  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.scrollTo({ top: 0 });
    }
  }, [hash]);

  // Auto-dismiss toast after 2.6s
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  const seededLwd = useMemo(
    () =>
      leeuwardenSystems.map((s) => ({
        kind: "lwd",
        id: s.id,
        code: s.code,
        name: s.name,
        tagline: s.tagline,
        icon: s.icon,
        riskLabel: s.riskClass,
        risk: s.riskClass.toLowerCase().includes("hoog")
          ? "high"
          : s.riskClass.toLowerCase().includes("midden")
          ? "medium"
          : "low",
        lifecycleStage: s.lifecycleStage,
        deletable: false,
      })),
    []
  );

  const userRows = useMemo(
    () =>
      userSystems.map((u) => ({
        kind: "user",
        id: u.id,
        code: u.code,
        name: u.name,
        tagline: u.domain,
        icon: Layers,
        riskLabel: u.riskLabel || RISK_LABEL[u.risk],
        risk: u.risk,
        lifecycleStage: u.published ? "Gepubliceerd" : "Concept",
        published: u.published,
        deletable: true,
      })),
    [userSystems]
  );

  const allRows = [...seededLwd, ...userRows];

  const askDelete = (row) => setConfirmTarget({ id: row.id, name: row.name });
  const cancelDelete = () => setConfirmTarget(null);
  const confirmDelete = () => {
    if (!confirmTarget) return;
    const removed = confirmTarget.name;
    deleteAssessment(confirmTarget.id);
    setUserSystems(loadAssessments());
    setConfirmTarget(null);
    setToast({ label: `Systeem verwijderd · ${removed}`, tone: "success" });
  };

  return (
    <div data-testid="page-platform">
      {/* HEADER */}
      <section className="relative overflow-hidden">
        <CircleGlow intensity={0.85} />
        <div className="rk-container relative pt-16 md:pt-24 pb-16">
          <div className="overline mb-6" data-testid="platform-eyebrow">
            Cockpit · VNG-lifecycle · Continu Gecertificeerd AI
          </div>
          <h1
            className="text-white tracking-tight leading-[1.05] max-w-5xl text-5xl md:text-7xl"
            style={{ fontFamily: "Fraunces, serif", fontWeight: 300 }}
            data-testid="platform-title"
          >
            De acht stappen,{" "}
            <em
              className="italic text-[#D4D8DF]"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              continu bewaakt.
            </em>
          </h1>
          <p className="mt-6 text-lg text-[#D4D8DF] max-w-2xl leading-relaxed">
            Volg het VNG-kader voor algoritme­governance van initiatief tot
            evaluatie. Elke stap kent een gereedheids indicator die zichtbaar
            maakt hoever de gemeente is op weg naar audit-proof werk.
          </p>
        </div>
        <div className="hairline" />
      </section>

      {/* READINESS GAUGES */}
      <section
        className="rk-container py-16"
        data-testid="platform-gauges"
        aria-label="Readiness per VNG-stap"
      >
        <div className="overline mb-10">Readiness · gemeente Leeuwarden</div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-y-12 gap-x-4 items-start">
          {vngSteps.map((s) => (
            <ReadinessGauge
              key={s.id}
              testid={`gauge-${s.id}`}
              value={READINESS[s.id] ?? 0}
              label={`${s.number} · ${s.title}`}
              caption="READINESS"
              size={140}
            />
          ))}
        </div>
      </section>

      <div className="hairline" />

      {/* BEHEERDE SYSTEMEN */}
      <section
        className="rk-container py-16"
        data-testid="platform-managed"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12">
          <div className="md:col-span-5">
            <div className="overline mb-4">Beheerde systemen</div>
            <h2
              className="text-3xl md:text-4xl text-white tracking-tight leading-tight"
              style={{ fontFamily: "Fraunces, serif", fontWeight: 300 }}
            >
              Alle algoritmes
              <br />
              <span
                className="italic text-[#D4D8DF]"
                style={{ fontFamily: "Fraunces, serif" }}
              >
                onder uw toezicht.
              </span>
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <p className="text-[#D4D8DF] leading-relaxed">
              Hieronder elk algoritme dat binnen RiskaiΛ geregistreerd staat.
              Vier zijn de Leeuwarden-baseline systemen uit deze handover. Andere
              regels zijn assessments die u via de intake heeft toegevoegd —
              die kunt u definitief verwijderen.
            </p>
          </div>
        </div>

        {/* Table-like list */}
        <div
          className="border border-[#C0C5CE]/20 overflow-x-auto"
          data-testid="managed-table"
        >
          <div
            className="hidden md:grid text-[10px] tracking-[0.22em] uppercase text-[#9AA4B2] px-6 py-4"
            style={{
              gridTemplateColumns: "minmax(0,3fr) 1.2fr 1.2fr 1fr auto",
              gap: "1.5rem",
              borderBottom: "1px solid rgba(192,197,206,0.15)",
            }}
          >
            <span>Systeem</span>
            <span>Domein</span>
            <span>Risico</span>
            <span>Status</span>
            <span className="text-right">Actie</span>
          </div>

          <ul role="list" className="min-w-[720px] md:min-w-0">
            {allRows.map((row) => {
              const Icon = row.icon;
              const tone = RISK_TONE[row.risk] || "#C0C5CE";
              return (
                <li
                  key={`${row.kind}-${row.id}`}
                  data-testid={`managed-row-${row.id}`}
                  className="px-6 py-5 grid items-center"
                  style={{
                    gridTemplateColumns:
                      "minmax(0,3fr) 1.2fr 1.2fr 1fr auto",
                    gap: "1.5rem",
                    borderBottom: "1px solid rgba(192,197,206,0.08)",
                  }}
                >
                  {/* Naam */}
                  <div className="flex items-center gap-4 min-w-0">
                    <div
                      className="w-10 h-10 border border-[#C0C5CE]/30 flex items-center justify-center shrink-0"
                      aria-hidden
                    >
                      <Icon
                        strokeWidth={1}
                        className="w-5 h-5 text-[#C0C5CE]"
                      />
                    </div>
                    <div className="min-w-0">
                      <div className="overline mb-0.5">{row.code}</div>
                      <div
                        className="text-white truncate"
                        style={{
                          fontFamily: "Fraunces, serif",
                          fontWeight: 400,
                          fontSize: "1.05rem",
                        }}
                        data-testid={`managed-name-${row.id}`}
                      >
                        {row.name}
                      </div>
                    </div>
                  </div>
                  {/* Domein */}
                  <div className="text-sm text-[#D4D8DF] truncate">
                    {row.tagline}
                  </div>
                  {/* Risico */}
                  <div className="text-sm" style={{ color: tone }}>
                    {row.riskLabel}
                  </div>
                  {/* Lifecycle */}
                  <div className="text-sm text-[#9AA4B2]">
                    {row.lifecycleStage}
                  </div>
                  {/* Actie */}
                  <div className="flex items-center justify-end gap-3">
                    <Link
                      to={`/platform/${row.id}`}
                      className="rk-btn-cyan text-[11px]"
                      style={{ padding: "0.5rem 1rem" }}
                      data-testid={`managed-open-${row.id}`}
                    >
                      Open
                      <ArrowUpRight
                        strokeWidth={1.25}
                        className="w-3.5 h-3.5"
                      />
                    </Link>
                    {row.deletable ? (
                      <button
                        type="button"
                        className="rk-btn-delete"
                        onClick={() => askDelete(row)}
                        data-testid={`managed-delete-${row.id}`}
                      >
                        <Trash2 strokeWidth={1.25} className="w-4 h-4" />
                        Verwijder systeem
                      </button>
                    ) : (
                      <span
                        className="text-[10px] tracking-[0.2em] uppercase text-[#9AA4B2]"
                        title="Onderdeel van Leeuwarden-handover; alleen RiskaiΛ-beheerder kan dit verwijderen."
                      >
                        Vast in handover
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      <div className="hairline" />

      {/* VNG TIMELINE */}
      <section className="rk-section pt-20 md:pt-24" id="vng">
        <div className="rk-container">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
            <div className="md:col-span-4">
              <div className="overline mb-4">Framework</div>
              <h2
                className="text-4xl md:text-5xl text-white tracking-tight leading-tight"
                style={{ fontFamily: "Fraunces, serif", fontWeight: 300 }}
                data-testid="platform-vng-heading"
              >
                VNG-lifecycle
                <br />
                <span
                  className="text-[#D4D8DF] italic"
                  style={{ fontFamily: "Fraunces, serif" }}
                >
                  in acht stappen.
                </span>
              </h2>
            </div>
            <div className="md:col-span-7 md:col-start-6">
              <p className="text-[#D4D8DF] leading-relaxed">
                Het VNG-kader structureert algoritme­governance in acht
                opeenvolgende stappen. Elke stap produceert artefacten die
                RiskaiΛ automatisch archiveert — van IAMA tot kill-switch.
                Zo is op elk moment reconstrueerbaar waarom een algoritme
                leeft, en waarom het blijft leven.
              </p>
            </div>
          </div>

          <div data-testid="platform-vng-timeline" className="max-w-4xl">
            {vngSteps.map((step, i) => (
              <VNGStep
                key={step.id}
                step={step}
                isLast={i === vngSteps.length - 1}
                index={i}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CONFIRM OVERLAY */}
      {confirmTarget && (
        <ConfirmDialog
          name={confirmTarget.name}
          onCancel={cancelDelete}
          onConfirm={confirmDelete}
        />
      )}

      {/* TOAST */}
      {toast && <Toast label={toast.label} tone={toast.tone} />}
    </div>
  );
};

// ---------------- ConfirmDialog ----------------
const ConfirmDialog = ({ name, onCancel, onConfirm }) => (
  <div
    role="dialog"
    aria-modal="true"
    aria-label="Bevestig verwijderen"
    data-testid="confirm-overlay"
    className="fixed inset-0 z-50 flex items-center justify-center"
    style={{ background: "rgba(15, 30, 51, 0.72)", backdropFilter: "blur(6px)" }}
    onClick={onCancel}
  >
    <div
      className="relative max-w-lg w-[92%] rk-fade-up"
      style={{
        background: "#162E4A",
        border: "1px solid rgba(192,197,206,0.25)",
        boxShadow: "0 30px 80px -20px rgba(0,0,0,0.6)",
        padding: "2.5rem 2rem 2rem",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={onCancel}
        className="absolute top-4 right-4 text-[#9AA4B2] hover:text-white"
        aria-label="Sluit"
        data-testid="confirm-close"
      >
        <X strokeWidth={1.25} className="w-5 h-5" />
      </button>

      <div className="flex items-start gap-4">
        <div
          className="w-12 h-12 border border-[#F08080]/55 flex items-center justify-center shrink-0"
          aria-hidden
        >
          <ShieldAlert
            strokeWidth={1}
            className="w-6 h-6"
            style={{ color: "#F08080" }}
          />
        </div>
        <div className="min-w-0">
          <div className="overline mb-3" style={{ color: "#F08080" }}>
            Bevestig verwijdering
          </div>
          <h2
            className="text-2xl md:text-3xl text-white tracking-tight"
            style={{ fontFamily: "Fraunces, serif", fontWeight: 300 }}
            data-testid="confirm-title"
          >
            Weet u zeker dat u{" "}
            <em
              className="italic text-[#D4D8DF]"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              {name}
            </em>{" "}
            definitief wilt verwijderen?
          </h2>
          <p className="mt-4 text-sm text-[#9AA4B2] leading-relaxed">
            Deze actie verwijdert het assessment, de bijbehorende
            register-publicatie en het volledige lineage. Het kan niet ongedaan
            worden gemaakt.
          </p>
        </div>
      </div>

      <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="rk-btn-ghost"
          data-testid="confirm-cancel"
          style={{
            justifyContent: "center",
            border: "1px solid rgba(192,197,206,0.4)",
            padding: "0.7rem 1.2rem",
          }}
        >
          Annuleren
        </button>
        <button
          type="button"
          onClick={onConfirm}
          className="rk-btn-danger"
          data-testid="confirm-delete"
          autoFocus
        >
          <Trash2 strokeWidth={1.25} className="w-4 h-4" />
          Definitief verwijderen
        </button>
      </div>
    </div>
  </div>
);

// ---------------- Toast ----------------
const Toast = ({ label, tone = "success" }) => {
  const isOk = tone === "success";
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="platform-toast"
      className="fixed bottom-8 right-8 z-50 rk-fade-up"
      style={{
        background: "#162E4A",
        border: `1px solid ${isOk ? "rgba(159,226,191,0.55)" : "rgba(240,128,128,0.55)"}`,
        padding: "0.85rem 1.1rem",
        display: "flex",
        alignItems: "center",
        gap: "0.7rem",
        boxShadow: "0 24px 48px -16px rgba(0,0,0,0.6)",
        maxWidth: "min(94vw, 420px)",
      }}
    >
      {isOk ? (
        <CheckCircle2
          strokeWidth={1.5}
          className="w-5 h-5"
          style={{ color: "#9FE2BF" }}
        />
      ) : (
        <ShieldAlert
          strokeWidth={1.5}
          className="w-5 h-5"
          style={{ color: "#F08080" }}
        />
      )}
      <span className="text-sm text-white">{label}</span>
    </div>
  );
};

export default Platform;
